import React, { useState } from 'react';
import {
  FDAccordion,
  FDAccordionDetails,
  FDAccordionTitle,
  FDAlert,
  FDBox,
  FDIcon,
  FDPaper,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import fr from 'facet-rest';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import {
  InvestmentMixCard,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedPortfolioUpdatesStepTemplate/InvestmentMixCard';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import {
  SecondaryActionButtonRel,
} from 'Packages/FacetCore/Experimental/RelationshipComponents/SecondaryActionButtonRel';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => FDTitleText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('disclaimer', {
    _component: () => FDAlert,
    _customProps: { size: 'lg' },
  }, overrideReadOnly);

const convertChartDataCollectionToObjectArray = (chartDataCollection) => {
  return chartDataCollection.resources().list().map((res) => ({
    color: res.attr('color'),
    label: res.attr('label'),
    name: res.attr('label'), // Using label as the name intentionally
    type: res.attr('type'),
    value: res.attr('value'),
  }));
};

const ModelUpdateCardLayout = ({ dataAttrs = {} }) => {
  const isMobile = useMobileLayout();
  const { resource } = useMetaLayoutContext();
  const { loadModalCollectionByLink, isInitializing } = useModalContext();
  const [open, setOpen] = useState(false);
  const currentInvestmentMixChartData = convertChartDataCollectionToObjectArray(resource.attr('currentInvestmentMixChartData'));
  const updatedInvestmentMixChartData = convertChartDataCollectionToObjectArray(resource.attr('updatedInvestmentMixChartData'));

  const showDisclaimer = resource.attr('disclaimer') !== undefined && resource.attr('disclaimer') !== null && resource.attr('disclaimer') !== '';
  return (
    <FDPaper width={'100%'} padding={1} {...dataAttrs}>
      <FDAccordion expanded={open} onChange={() => setOpen(!open)}>
        <FDAccordionTitle>
          <FDStack direction={'row'} alignItems={'center'} spacing={1}>
            <FDIcon icon={resource.attr('titleIcon') || 'trendingUp'} />
            <MetaControlField attr={'title'} />
          </FDStack>
        </FDAccordionTitle>
        <FDAccordionDetails>
          <FDStack direction={'column'} spacing={2} width={'100%'}>
            <FDStack
              direction={isMobile ? 'column' : 'row'}
              spacing={isMobile ? 2 : 6}
              width={'100%'}
              justifyContent={isMobile ? undefined : 'space-between'}
            >
              <FDBox maxWidth={'50%'} height={'100%'} width={'100%'}>
                <InvestmentMixCard investmentMixChartData={currentInvestmentMixChartData || []} attributes={resource.attr('currentAttributes')} />
              </FDBox>
              <FDBox maxWidth={'50%'} height={'100%'} width={'100%'}>
                <InvestmentMixCard investmentMixChartData={updatedInvestmentMixChartData || []} isUpdated attributes={resource.attr('updatedAttributes')} />
              </FDBox>
            </FDStack>
            {showDisclaimer && (
              <FDAlert
                status={'info'}
                description={resource.attr('disclaimer')}
              />
            )}
            <FDBox>
              {resource.withRelationship(
                fr.relationships.details.name,
                (rel) => (
                  <SecondaryActionButtonRel
                    relationship={rel}
                    size={'sm'}
                    overrideOnClick={() => loadModalCollectionByLink(rel.link('related').url())}
                    loading={isInitializing}
                  />
                ),
                () => null,
              )}
            </FDBox>
          </FDStack>
        </FDAccordionDetails>
      </FDAccordion>
    </FDPaper>
  );
};

const ModelUpdateCard = ({ modelUpdateResource, dataAttrs }) => {
  return (
    <MetaResourceRenderer
      resource={modelUpdateResource}
      layout={ModelUpdateCardLayout}
      metaOverrideSet={metaOverrideSet}
      LayoutProps={{
        dataAttrs,
      }}
    />
  );
};

ModelUpdateCard.propTypes = {
  dataAttrs: PropTypes.object,
  modelUpdateResource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export { ModelUpdateCard };
